window.dn = window.dn || {};

/**
 * All code related to realtime communication, e.g. WebSockets.
 */

(function (realtime, $, undefined)
{
    /**
     * If an open socket is closed, and this is true, re-open it.
     */
    var subscriptionSocketKeepOpen = true;

    /**
     * If an open socket is closed, and this is true, re-open it after this number of milliseconds.
     */
    var subscriptionSocketKeepOpenDelayMilliseconds = 3000;

    /**
     * Keep track of any callbacks that get registered (in `Connect()`) for socket events.
     */
    realtime.Callbacks = 
    { 
        OnClose: null,
        OnError: null,
        OnMessage: null,
        OnOpen: null
    };

    /**
     * This is where we'll store any action handlers that are registered. When an incoming
     * message is received, we'll check here for any handlers registered for the `message.Category`
     * and `message.Action`, and if found, we'll execute the handler, passing in the `message` object. 
     * You can register action handlers using `realtime.RegisterHandler()`.
     * 
     * Note that only one callback can be registered for a given category and action.
     */
    realtime.MessageHandlers = {};

    /**
     * A bitflag enum telling us what kind of message this is. Each MessageType flag must have
     * the Agent or Customer bitflag set, and then one of the others (Ended, Started, Typing).
     * 
     * Some kinds of messages might not even be stored in the database, e.g. we might decide
     * to just relay Typing message indicators via WebSockets with no storage.
     * 
     * NOTE: Keep this in sync with the C# IVS.API.Models.Chat.MessageType enum.
     */
    realtime.MessageType = 
    {
        /**
         * This message originated with the agent.
         */
        Agent: 1, 

        /**
         * This message originated with the customer.
         */
        Customer: 2,

        /**
         * This message indicates the session is now over. Receiving one of these
         * will likely cause the Session to be flagged as `Active=false`.
         */
        Ended: 4,

        /**
         * This indicates a normal message.
         */
        Message: 8,

        /**
         * This message indicates the start of a session. The session will be flagged
         * `IsActive=true` if it already existed.
         */
        Started: 16,

        /**
         * Indicates this message arrived via SMS or MMS texting.
         */
        TransportSms: 32,

        /**
        /// Indicates this message arrived via the web.
         */
        TransportWeb: 64,

        /**
         * This message indicates the person is typing. We don't store these in the database.
         * They are only passed through to websockets monitoring the realtime session.
         */
        Typing: 128
    };

    /** 
     * Max number of errors we will allow before we abort any socket connections.
     */
    realtime.MaxErrors = 20;

    /** 
     * Current number of errors encountered. When we hit realtime.MaxErrors, we won't attempt
     * to connect or reconnect to WebSockets. This is to prevent a runaway error.
     */
    realtime.NumErrors = 0;

    /**
     * Stores a reference to our WebSocket used for tracking the status
     * of this realtime session, getting alerted to new messages, etc.
     */
    realtime.Socket = null;

    /**
     * If the WebSocket realtime.Socket has been opened, this is the date it was opened.
     */
    realtime.SocketDateOpened = null;

    /**
     * Connect to our realtime server to obtain immediate, realtime notifications.
     * 
     * To stop, call realtime.Disconnect().
     * 
     * @param {object}      params:            The options for this method, including:
     * @param {func}        params.onOpen:     The function called when the websocket is opened.
     * @param {func}        params.onClose:    The function called when the websocket is closed (normally).
     * @param {func}        params.onError:    The function called when the websocket is closed (due to an error).
     * @param {func}        params.onMessage:  The function we'll call when we have received a realtime message.
     */
    realtime.Connect = dn.Debounce(function(params)
    {
        if (!dn.user.IsLoggedIn)
        {
            console.debug("realtime.Connect()", "Aborting connection because user is anonymous.", dn.user);
            return;
        }

        if (realtime.NumErrors > realtime.MaxErrors)
        {
            console.error("realtime.Connect()", "Aborting connections due to having " + realtime.NumErrors + " errors. Max = " + realtime.MaxErrors);
            realtime.Disconnect();
            return;
        }

        if (params)
        {
            // Typically only set on the initial connection attempt; not retries.
            if (params.onClose)
            {
                realtime.Callbacks.OnClose = params.onClose;
            }
            if (params.onError)
            {
                realtime.Callbacks.OnError = params.onError;
            }
            if (params.onMessage)
            {
                realtime.Callbacks.OnMessage = params.onMessage;
            }
            if (params.onOpen)
            {
                realtime.Callbacks.OnOpen = params.onOpen;
            }
        }

        if (!realtime.Socket || realtime.Socket.readyState > WebSocket.OPEN) // > OPEN means CLOSING/CLOSED, not CONNECTING/OPEN. See: https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState
        {
            console.debug("realtime.Connect()", "Connecting...", params);

            // Establish a WebSocket connection to listen for updates on this session
            subscriptionSocketKeepOpen = true;
            realtime.Socket = new WebSocket("wss://" + dn.constants.DomainRealtime + "/?auth=" + encodeURIComponent(dn.constants.ApiKeyTokenProviderName + " " + dn.constants.ApiKeyTokenUserType + "." + dn.user.Email + ":" + dn.user.ApiToken));

            realtime.Socket.onopen = function (event) 
            {
                console.debug("realtime.Socket.onopen", event);
                realtime.SocketDateOpened = new Date();

                if (realtime.Callbacks.OnOpen)
                {
                    realtime.Callbacks.OnOpen(event);
                }
            };

            // When the realtime.Socket is closed, try to re-open it after waiting 3 seconds
            realtime.Socket.onclose = function (event) 
            {
                console.debug("realtime.Socket.onclose", "Socket closed " + (realtime.SocketDateOpened ? " after " + ((new Date() - realtime.SocketDateOpened) / 1000) + " seconds open)" : "") + ": " + event.reason + " (" + event.code + "/clean=" + event.wasClean + "). " + (subscriptionSocketKeepOpen ? "Re-opening in " + subscriptionSocketKeepOpenDelayMilliseconds + " milliseconds." : ""), event);
                realtime.Socket = null;

                if (subscriptionSocketKeepOpen)
                {
                    setTimeout(dn.realtime.Connect, subscriptionSocketKeepOpenDelayMilliseconds);
                }

                if (realtime.Callbacks.OnClose)
                {
                    realtime.Callbacks.OnClose(event);
                }
            };

            // For realtime.Socket errors, check if the realtime.Socket is closed, and if so, prompt the user to see if they want to reopen it.
            realtime.Socket.onerror = function (event) 
            {
                realtime.NumErrors++;

                if (!realtime.Socket || realtime.Socket.readyState > WebSocket.OPEN) // > OPEN means CLOSING/CLOSED, not CONNECTING/OPEN. See: https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState
                {
                    console.warn("realtime.Socket.onerror", realtime.NumErrors, (subscriptionSocketKeepOpen ? "Re-opening in " + subscriptionSocketKeepOpenDelayMilliseconds + " milliseconds." : ""), event);
                    realtime.Socket = null;

                    if (subscriptionSocketKeepOpen)
                    {
                        setTimeout(dn.realtime.Connect, subscriptionSocketKeepOpenDelayMilliseconds);
                    }
                }
                else
                {
                    console.warn("realtime.Socket.onerror", realtime.NumErrors, "realtime.Socket error, but still open.", event);
                }

                if (realtime.Callbacks.OnError)
                {
                    realtime.Callbacks.OnError(event);
                }
            };

            // This is where we'll hear about any notifications on devices, sessions, and tickets
            realtime.Socket.onmessage = function (event) 
            {
                console.debug("realtime.Socket.onmessage", event);

                if (event.data)
                {
                    var message = JSON.parse(event.data);

                    realtime.HandleMessage(message);

                    if (realtime.Callbacks.OnMessage)
                    {
                        realtime.Callbacks.OnMessage(message);
                    }
                }
            };
        }
        else
        {
            console.debug("realtime.Connect()", "Ignoring since already connected/connecting...", realtime.Socket.readyState);
        }
    }, 5000, true);

    /**
     * Disconnect from our realtime server stop receiving realtime notifications.
     */
    realtime.Disconnect = function()
    {
        console.debug("realtime.Disconnect()");

        subscriptionSocketKeepOpen = false; // so we don't restart the connection unless requested

        if (realtime.Socket && realtime.Socket.readyState < WebSocket.CLOSING) // < CLOSING means CONNECTING/OPEN, not CLOSING/CLOSED. See: https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState
        {
            realtime.Socket.close();
            realtime.Socket = null;
        }
    };

    /**
     * When our websocket receives real-time messages, they are processed here.
     * 
     * @param {object}  message:   The message received from our realtime service (WebSockets).
     */
    realtime.HandleMessage = function(message)
    {
        console.debug("realtime.HandleMessage()", message);

        var callbackKey = '' + message.Category + '/' + message.Action;

        if (message.NumUnreadChatReplies)
        {
            // globally we'll handle any changes needed to reflect the number of unread chat replies in the UI
            dn.UnreadChatRepliesSet(message.NumUnreadChatReplies);
        }

        if (realtime.MessageHandlers[callbackKey])
        {
            console.debug("realtime.HandleMessage()", 'Calling callback for ' + callbackKey);
            realtime.MessageHandlers[callbackKey](message);
        }
        else
        {
            console.warn("realtime.HandleMessage()", 'No callback registered for ' + callbackKey);
        }
    };

    /**
     * Register an action handler for a specific action. If an incoming realtime message
     * has the given `action` as the `message.Action`, then the `handler` function will be
     * executed, passing in the `message` object.
     */
    realtime.RegisterHandler = function(category, action, callback)
    {
        console.debug("realtime.RegisterHandler()", category, action);

        realtime.MessageHandlers[category + '/' + action] = callback;
    };

    /**
     * When our websocket receives real-time messages, they are processed here.
     * 
     * @param {object}  message:   The message to send via our realtime connection.
     */
    realtime.SendMessage = function(message)
    {
        console.debug("realtime.SendMessage()", message);

        realtime.Socket.send(JSON.stringify(message));
    };

    $(document).ready(function()
    {
        dn.realtime.Connect();

        // Register a handler for when this user receives a PM message.
        dn.realtime.RegisterHandler
        (
            dn.constants.EventCategory.Chat,
            dn.constants.EventAction.Notified,
            function(message)
            {
                // See if we're on a PM page and the message is of the same TypeId, before inserting.
                if (window.location.href.startsWith(dn.constants.DiscussionTypes[message.Data.Message.TypeId].Link))
                {
                    // A notification about a new chat message
                    dn.chat.InsertMessages(message.Data.Message.DiscussionKey, [message.Data.Message], message.Data.TemporaryKey, (message.Data.Message.UserKey === dn.user.UserKey ? "You" : message.Data.Message.Author.FirstName) + ": " + message.Data.Message.Snippet, true, true);
                }
                else
                {
                    // We're not on the page, so just show a popup notification that fades away (toast)
                    dn.ShowNotification({
                        title: '<img src="' + message.Data.Message.Author.UrlProfilePhoto + '" style="width: 20px; height: 20px; border-radius: 50%"/> ' + message.Data.Message.Author.FirstName + ' ' + message.Data.Message.Author.LastName,
                        body: message.Data.Message.Snippet,
                        autohide: false,
                        className: 'clickable',
                        onClick: function(event, $toast)
                        {
                            // Open the chat page for this message
                            window.location.href = message.Data.Message.Link;
                            $toast.toast('hide');
                        }
                    });
                }
            }
        );
    });

}(window.dn.realtime = window.dn.realtime || {}, jQuery));
